import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SYUSER } from './cognito.service';
import { jwtDecode } from 'jwt-decode';

export interface userCallStructure{
  data: SYUSER[]
}

interface JwtPayload {
  userid: string;
  email: string;
  firstname: string;
  lastname: string;
  eid: string;
  preferences: { langid: string, location: string };
  certified: null | any;
  accesstoken: { exp: number; iat: number };
  refresh_token: { exp: number; iat: number };
  roles: { roles: string }[];
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  public token: string = '';
  public redirect: string = '';
  public locationUpdateMessage: string = '';

  constructor(
    private http: HttpClient) { }

  /**
   * Authenticates the user with the provided email.
   * @param email The email of the user to authenticate.
   * @returns An Observable emitting the redirect destination upon successful authentication or an error message.
  */

   async authenticateUser(email: string): Promise<string | boolean> {
    const url = `${environment.api.cdn}${environment.api.route.authenticate}`;
    let userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if(userTimeZone == null || userTimeZone == ''){
      userTimeZone = 'UTC';
    }

    const payload = {
      email: email,
      timeZone: userTimeZone
    };

    const response = await firstValueFrom(this.http.post<any>(url, payload));

    const redirectDestination = response.redirect;
    this.redirect = redirectDestination;
    this.token = response.token;
    const ulock  = parseInt(response.ulock);
    //console.log(ulock)

    if(ulock === 1){
      return false;
    }

    return redirectDestination;

  }

  // Function to decode a JWT token
  decodeToken(token: string | ''): JwtPayload {
    // Decode the JWT token
    const decodedToken = jwtDecode(token);

    // Return the payload information
    return decodedToken as JwtPayload;
  }

 // Function to get all SYUSERs and return them as a promise
 async getSYUSERS(): Promise<SYUSER[]> {
    const url = `${environment.api.cdn}${environment.api.route.getUsers}`;

    try {
      const response: userCallStructure = await firstValueFrom(this.http.get<userCallStructure>(url));
      return response.data;  // This will return the array of SYUSERs
    } catch (error) {
      console.error('Error fetching SYUSER data:', error);
      throw new Error('Failed to fetch SYUSER data');
    }
  }

  // Function to delete selected users
  async deleteSelectedUser(usernames: string[]): Promise<any> {
    const url = `${environment.api.cdn}${environment.api.route.deleteUsers}`; // Replace with the actual API route for deleting users

    // Define the HTTP headers with content type
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });

    try {
      // Send the list of usernames to the API
      return await firstValueFrom(
        this.http.post<any>(url, { usernames })
      );

    } catch (error) {
      console.error('Error deleting selected users:', error);
      throw new Error('Failed to delete selected users');
    }
  }

  async changeUserLocation(user: any, location: string){
    const url = `${environment.api.cdn}${environment.api.route.changeUserLocation}`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });

    try {
      const params = {
        email: user.email,
        location: location
      }

      const response = await firstValueFrom(
        this.http.post<void>(url, { params })
      );
      const message = JSON.parse(JSON.stringify(response));

      if(message.success){
        this.locationUpdateMessage = 'alert-success';
        user.syuser.location = location;
      }else{
        this.locationUpdateMessage = 'alert-danger';
      }
    }catch (error) {
      console.error('Error deleting selected users:', error);
      throw new Error('Failed to delete selected users');
    }
  }

  async checkUserEmail(email: string) {
    const url = `${environment.api.cdn}${environment.api.route.checkUserEmail}`;
    const payload = {
      email: email
    };
    try {
      return await firstValueFrom(this.http.post<any>(url, payload));
    } catch (error) {
      console.error('Error checking user email:', error);
    }
  }
}
